import { Box, Drawer, IconButton, Typography, useTheme } from "@mui/material";
import { X } from "lucide-react";
import { ActivityItem } from "@/modules/lead-page/dtos/index-v2";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import MobileModal from "@/modules/common/components/ui-kit/modals/mobile-modal";
import ZoomCallDetailBody from "@/modules/zoom-calls/components/zoom-call-detail-body";

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

interface ZoomCallDetailModalProps {
  open: boolean;
  onClose: () => void;
  zoomCallUid: string;
  activityItem: ActivityItem;
}

/* -------------------------------------------------------------------------- */
/*  Main component                                                            */
/* -------------------------------------------------------------------------- */

const DRAWER_WIDTH = "460px";

const ZoomCallDetailModal = ({
  open,
  onClose,
  zoomCallUid,
  activityItem,
}: ZoomCallDetailModalProps) => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();

  /* ----- Mobile: bottom sheet --------------------------------------------- */

  if (isMobile) {
    return (
      <MobileModal
        open={open}
        onClose={onClose}
        title="Call details"
        snapPoints={[1]}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            width: "100%",
            px: theme.nawy.spacing.md,
            py: theme.nawy.spacing.md,
          }}
        >
          {open && (
            <ZoomCallDetailBody
              zoomCallUid={zoomCallUid}
              activityItem={activityItem}
            />
          )}
        </Box>
      </MobileModal>
    );
  }

  /* ----- Desktop: right-anchored Drawer ----------------------------------- */

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 1400,
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          backgroundColor: theme.nawy.colors.semantic.layer.primary,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          zIndex: 1400,
        },
      }}
    >
      {/* Header — 52px, "Call details" + X, border-bottom */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: theme.nawy.spacing.md,
          height: 52,
          px: theme.nawy.spacing.md,
          py: theme.nawy.spacing.xs,
          borderBottom: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            ...theme.nawy.typography.english.variants.headline.xs,
            color: theme.nawy.colors.semantic.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          Call details
        </Typography>

        <IconButton
          onClick={onClose}
          sx={{
            width: 36,
            height: 36,
            borderRadius: "8px",
            color: theme.nawy.colors.semantic.text.primary,
            flexShrink: 0,
            px: "10px",
            "&:hover": {
              backgroundColor: theme.nawy.colors.semantic.layer.secondary,
            },
          }}
        >
          <X size={16} />
        </IconButton>
      </Box>

      {/* Scrollable content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: theme.nawy.spacing.md,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            background: theme.nawy.colors.semantic.border.secondary,
            borderRadius: 3,
          },
        }}
      >
        {open && (
          <ZoomCallDetailBody
            zoomCallUid={zoomCallUid}
            activityItem={activityItem}
          />
        )}
      </Box>
    </Drawer>
  );
};

export default ZoomCallDetailModal;
